import * as React from "react";
import "./Prompt.css";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";

import ButtonComponent from "../../components/uikit/components/Button/ButtonComponent";
import CustomTooltip from "../../components/uikit/components/Tooltip/Tooltip";
import AddPrimaryThemeForm from "./AddPrimaryThemes/AddPrimaryThemes";
import SavePromptModal from "../../components/uikit/components/modals/SavePromptModal/SavePromptModal";

import save from "../../assets/images/save.svg"; 
import cancel from "../../assets/images/cancel.svg";

import {
  closeSavePromptModal,
  openSavePromptModal,
  currentPromptRow,
} from "../../features/Modal/ModalSlice";
import { formatRows } from "../../constants/helper";

const PrimaryThemes = ({ primaryData, promptId }) => {
  const dispatch = useDispatch();
  const savedRows = useSelector((state) => state?.ModalSlice?.currentPromptRow);
  const currentFormValues = useSelector((state) => state?.product?.currentFormValues);

  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [showAddTheme, setShowAddTheme] = React.useState(false);
console.log(promptId)
  React.useEffect(() => {
    if (savedRows?.length > 0) {
      setRows(savedRows);
    } else if (primaryData?.length > 0) {
      const themeData = primaryData[0]?.theme_data || [];
      const formattedData = themeData.map((item, index) => ({
        id: item.key + index,
        primaryTheme: item.key,
        focus: item.value,
      }));
      setRows(formattedData);
    }
  }, [primaryData, savedRows]);

  const handleRowActions = {
    editStop: (params, event) => {
      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
        event.defaultMuiPrevented = true;
      }
    },

    edit: (id) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
      handleMenuClose();
    },

    save: (id) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
      handleMenuClose();
    },

    delete: (id) => () => {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      dispatch(currentPromptRow(updatedRows));
      handleMenuClose();
    },

    cancel: (id) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });

      const editedRow = rows.find((row) => row.id === id);
      if (editedRow.isNew) {
        setRows(rows.filter((row) => row.id !== id));
      }
    },

    processUpdate: (newRow) => {
      const updatedRow = { ...newRow, isNew: false };
      const updatedRows = rows.map((row) => 
        row.id === newRow.id ? updatedRow : row
      );
      setRows(updatedRows);
      dispatch(currentPromptRow(updatedRows));
      return updatedRow;
    },

    modesModelChange: (newRowModesModel) => {
      setRowModesModel(newRowModesModel);
    }
  };

  const handleMenuClick = (event, id) => {
    if (rowModesModel[id]?.mode !== GridRowModes.Edit) {
      if (anchorEl !== event.currentTarget) {
        setSelectedRow(id);
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleSavePrompt = () => dispatch(openSavePromptModal());

  const handleModalClose = () => dispatch(closeSavePromptModal());

  const handleSaveNewTheme = (primaryTheme, focus) => {
    if (!primaryTheme || !focus) return;

    const newRow = {
      id: `${primaryTheme}-${rows.length}`,
      primaryTheme,
      focus,
    };

    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    dispatch(currentPromptRow(updatedRows));
    setShowAddTheme(false);
  };

  const replaceVariables = (value) => {
    if (!value) return value;
    
    let replacedValue = value;
    if (value.includes('{replacementAssetVariable}') && currentFormValues?.asset_name) {
      replacedValue = replacedValue.replace('{replacementAssetVariable}', currentFormValues.asset_name);
    }
    if (value.includes('{replacementIndicationVariable}') && currentFormValues?.indication_name) {
      replacedValue = replacedValue.replace('{replacementIndicationVariable}', currentFormValues.indication_name);
    }
    return replacedValue;
  };

  const columns = [
    {
      field: "primaryTheme",
      headerName: "Primary Theme",
      flex: 3,
      editable: true,
      renderCell: (params) => {
        const displayValue = replaceVariables(params.value);
        return (
          <CustomTooltip title={displayValue}>
            <span className="primary-theme-row text-truncate">
              {displayValue}
            </span>
          </CustomTooltip>
        );
      },
    },
    {
      field: "focus", 
      headerName: "Focus",
      flex: 6,
      editable: true,
      renderCell: (params) => (
        <CustomTooltip title={params.value}>
          <span className="focus-rows">{params.value}</span>
        </CustomTooltip>
      ),
    },
    {
      field: "actions",
      type: "actions", 
      headerName: "",
      flex: 1.2,
      cellClassName: "actions",
      renderCell: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        return (
          <>
            {isInEditMode ? (
              <div className="d-flex gap-2 w-full">
                <div className="save-prompt-tick">
                  <GridActionsCellItem
                    icon={<img src={save} alt="save" style={{width: "15px", height: "15px"}}/>}
                    label="Save"
                    sx={{color: "primary.main"}}
                    onClick={handleRowActions.save(id)}
                  />
                </div>
                <div className="cancel-prompt-tick">
                  <GridActionsCellItem
                    icon={<img src={cancel} alt="cancel" style={{width: "15px", height: "15px"}}/>}
                    label="Cancel"
                    onClick={handleRowActions.cancel(id)}
                  />
                </div>
              </div>
            ) : (
              <GridActionsCellItem
                icon={<MoreVertIcon />}
                label="Actions"
                onClick={(event) => handleMenuClick(event, id)}
              />
            )}

            <Menu
              id="actions-menu"
              className="action-box"
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedRow === id}
              onClose={handleMenuClose}
              sx={{
                boxShadow: "none",
                borderRadius: 4,
                p: 2,
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              onClick={handleMenuClose}
            >
              {!isInEditMode && (
                <>
                  <MenuItem
                    className="d-flex popListItem gap-[10px]"
                    sx={{
                      "&:hover": {
                        backgroundColor: "#eeedfd",
                        color: "#4F46E5",
                        fontWeight: "600 !important",
                      },
                    }}
                    onClick={handleRowActions.edit(id)}
                  >
                    <EditIcon /> Edit
                  </MenuItem>
                  <MenuItem
                    className="d-flex popListItem gap-[10px]"
                    sx={{
                      "&:hover": {
                        backgroundColor: "#feecef",
                        color: "#F43F5E",
                        fontWeight: "500 !important",
                      },
                    }}
                    onClick={handleRowActions.delete(id)}
                  >
                    <DeleteIcon /> Delete
                  </MenuItem>
                </>
              )}
            </Menu>
          </>
        );
      },
    },
  ];

  const formattedRows = formatRows(rows);

  return (
    <div className="themes-table-container">
      <div className="add-primary-theme">
        <Button
          className="add-primary-btn"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowAddTheme(true)}
        >
          Add primary theme
        </Button>
      </div>

      {showAddTheme && (
        <div className="add-primary-theme-form">
          <AddPrimaryThemeForm
            onSave={handleSaveNewTheme}
            onClose={() => setShowAddTheme(false)}
          />
        </div>
      )}

      <div className={`prompt-table ${showAddTheme ? "open-prompt-table" : ""}`}>
        <Box
          sx={{
            height: 500,
            width: "100%",
            "& .actions": {
              color: "text.secondary",
            },
            "& .textPrimary": {
              color: "text.primary",
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowActions.modesModelChange}
            onRowEditStop={handleRowActions.editStop}
            processRowUpdate={handleRowActions.processUpdate}
            slotProps={{
              toolbar: { setRows, setRowModesModel },
            }}
            hideFooter={true}
          />
          <div className="save-prompt">
            <ButtonComponent
              variant="contained"
              onClick={handleSavePrompt}
              btnType="primary"
              text="Save Prompt as"
              className="btnPrimary"
            />
          </div>
        </Box>

        <SavePromptModal
          closeModal={handleModalClose}
          promptId={promptId}
          themeData={formattedRows}
          themename={primaryData[0]?.name}
        />
      </div>
    </div>
  );
};

export default PrimaryThemes;
